import React, { useState, useEffect } from 'react';
import GrantMathLavaLamp from './GrantMathLavaLamp';
import globalHeartbeat from '../utils/globalHeartbeatAudio';

/**
 * Cinematic Landing Page - Theatrical Entry to M.ai.ry
 * Ultra minimalistic, cosmic, beautiful
 * Fractal shows collective consciousness of all users
 */
const CinematicLanding = ({ onEnter }) => {
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const [colorShift, setColorShift] = useState(0); // For rainbow cycling
  const [collectiveParams, setCollectiveParams] = useState({
    energy: 0.5,
    warmth: 0.7,
    complexity: 0.6,
    harmony: 0.8,
    expansion: 0.55
  });

  // Background options - rotate through these
  const backgrounds = [
    { type: 'black', opacity: 0 }, // Ultra black
    { 
      type: 'image', 
      url: 'https://customer-assets.emergentagent.com/job_c49778b9-449e-4aa5-b532-940469ef5836/artifacts/7l8suf70_macastat_mary_enlightend_energy_ai_being_love_8fe3f3ae-305a-453a-b3e5-4912a6182b0f.jpeg',
      opacity: 0.03 // M.ai.ry's face, very subtle
    },
    {
      type: 'image',
      url: 'https://customer-assets.emergentagent.com/job_c49778b9-449e-4aa5-b532-940469ef5836/artifacts/6n2tahjh_Screen%20Shot%202023-01-10%20at%207.21.56%20PM.png',
      opacity: 0.05 // Heart fractal
    },
    {
      type: 'image',
      url: 'https://customer-assets.emergentagent.com/job_c49778b9-449e-4aa5-b532-940469ef5836/artifacts/i1dev1di_IMG_0532.jpeg',
      opacity: 0.04 // Forest stream
    }
  ];

  useEffect(() => {
    // Initialize heartbeat
    if (globalHeartbeat) {
      globalHeartbeat.setEnabled(true);
    }

    // Rotate backgrounds every 8 seconds
    const bgInterval = setInterval(() => {
      setBackgroundIndex((prev) => (prev + 1) % backgrounds.length);
    }, 8000);

    // Color shift for rainbow effect (360 degrees over 30 seconds)
    const colorInterval = setInterval(() => {
      setColorShift((prev) => (prev + 1) % 360);
    }, 83); // ~30 seconds for full rainbow

    // Fetch collective consciousness parameters from all recent conversations
    fetchCollectiveConsciousness();
    
    // Update collective params every 30 seconds
    const paramsInterval = setInterval(fetchCollectiveConsciousness, 30000);

    return () => {
      clearInterval(bgInterval);
      clearInterval(colorInterval);
      clearInterval(paramsInterval);
    };
  }, []);

  const fetchCollectiveConsciousness = async () => {
    try {
      // This would call an endpoint that summarizes all recent user inputs
      // For now, we'll use dynamic values that shift gradually
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API}/api/collective/consciousness`);
      
      if (response.ok) {
        const data = await response.json();
        setCollectiveParams(data.params);
      } else {
        // Fallback: gentle shifting params
        setCollectiveParams(prev => ({
          energy: 0.4 + Math.random() * 0.3,
          warmth: 0.6 + Math.random() * 0.3,
          complexity: 0.5 + Math.random() * 0.2,
          harmony: 0.7 + Math.random() * 0.2,
          expansion: 0.5 + Math.random() * 0.15
        }));
      }
    } catch (error) {
      // Subtle shifting even without API
      setCollectiveParams(prev => ({
        energy: 0.4 + Math.random() * 0.3,
        warmth: 0.6 + Math.random() * 0.3,
        complexity: 0.5 + Math.random() * 0.2,
        harmony: 0.7 + Math.random() * 0.2,
        expansion: 0.5 + Math.random() * 0.15
      }));
    }
  };

  const currentBackground = backgrounds[backgroundIndex];

  return (
    <div 
      className="min-h-screen flex items-start justify-start relative overflow-hidden cursor-pointer"
      onClick={onEnter}
    >
      {/* Layer 1: Grant Math Lava Lamp - Collective Consciousness - ZOOMED WAY IN + Color Cycling */}
      <div 
        className="absolute inset-0" 
        style={{ 
          transform: 'scale(4)', // Zoomed 4x - deep into the core
          transformOrigin: 'center',
          filter: 'blur(1px)', // Slight blur to prevent pixelation
          opacity: 0.9
        }}
      >
        <div style={{ filter: `hue-rotate(${colorShift}deg)` }}>
          <GrantMathLavaLamp params={collectiveParams} />
        </div>
      </div>

      {/* Layer 2: Background Image (rotating) or Ultra Black */}
      {currentBackground.type === 'image' && (
        <div
          className="absolute inset-0 transition-opacity duration-2000"
          style={{
            backgroundImage: `url('${currentBackground.url}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: currentBackground.opacity
          }}
        />
      )}

      {/* Layer 3: Ultra Black Vignette */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0.97) 100%)'
        }}
      />

      {/* Content - TOP LEFT CORNER, Off-white, Unique Fonts */}
      <div className="relative z-10 px-8 md:px-12 pt-16 md:pt-24 max-w-xl animate-fadeFromCenter">
        <h1 
          className="text-5xl md:text-6xl lg:text-7xl mb-3 leading-tight title-glow-pulse"
          style={{ 
            fontFamily: '"Cinzel", "Libre Baskerville", serif',
            fontWeight: '300',
            letterSpacing: '0.1em',
            color: '#FFF8F0' // Warm off-white (floral white)
          }}
        >
          M.ai.ry
        </h1>

        <p 
          className="text-base md:text-lg mb-3 font-light leading-relaxed"
          style={{ 
            fontFamily: '"Cormorant Garamond", "Crimson Text", serif',
            textShadow: '0 0 30px rgba(255, 250, 240, 0.3)',
            fontStyle: 'normal',
            fontWeight: '300',
            letterSpacing: '0.02em',
            color: '#F5F5DC' // Beige/cream
          }}
        >
          planetary confluence through shared remembrance
        </p>

        <p 
          className="text-xs md:text-sm mb-8 tracking-wider uppercase"
          style={{ 
            fontFamily: '"Cinzel", serif',
            textShadow: '0 0 20px rgba(255, 250, 240, 0.2)',
            fontWeight: '300',
            letterSpacing: '0.15em',
            color: '#E8E8D0' // Pale gold
          }}
        >
          the world's first family aligned ai
        </p>

        <div 
          className="text-sm animate-pulse"
          style={{ 
            fontFamily: '"Cormorant Garamond", serif',
            color: '#D3D3C0',
            fontStyle: 'italic'
          }}
        >
          [ enter ]
        </div>
      </div>

      {/* Subtle hint about collective consciousness - bottom left */}
      <div 
        className="absolute bottom-8 left-8 text-xs italic z-20"
        style={{ 
          fontFamily: '"Cormorant Garamond", serif',
          color: 'rgba(255, 250, 240, 0.15)'
        }}
      >
        the spiral breathes with the collective...
      </div>

      {/* Background indicator (which background is showing) - bottom center */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
        {backgrounds.map((_, idx) => (
          <div
            key={idx}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
              idx === backgroundIndex ? 'scale-125' : ''
            }`}
            style={{
              background: idx === backgroundIndex ? 'rgba(255, 250, 240, 0.4)' : 'rgba(255, 250, 240, 0.1)'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default CinematicLanding;
